// frontend/src/components/admin/PreferenceAnalytics.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Building,
  Database,
  AlertCircle,
  Target,
  Percent,
  Calendar,
  RefreshCw
} from 'lucide-react';

/**
 * PreferenceAnalytics Component
 * Analyze preference usage patterns and trends
 */
const PreferenceAnalytics = ({ userRole }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [analysisData, setAnalysisData] = useState({
    users_with_custom_preferences: [],
    entities_with_custom_settings: [],
    unused_preference_definitions: [],
    popular_preferences: []
  });
  const [usageStats, setUsageStats] = useState([]);
  const [auditStats, setAuditStats] = useState({
    period_days: 30,
    daily_stats: [],
    top_users: [],
    top_preferences: []
  });
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      const entityId = localStorage.getItem('entityId');
      
      // Fetch analytics data in parallel
      const [analysisResponse, usageResponse, auditResponse] = await Promise.all([
        axios.get(`/api/admin/preferences/${entityId}/analysis`),
        axios.get(`/api/admin/preferences/${entityId}/usage`),
        axios.get(`/api/admin/preferences/${entityId}/audit/stats?days=${selectedPeriod}`)
      ]);

      if (analysisResponse.data.success) {
        setAnalysisData(analysisResponse.data.data);
      }
      
      if (usageResponse.data.success) {
        setUsageStats(usageResponse.data.data);
      }
      
      if (auditResponse.data.success) {
        setAuditStats(auditResponse.data.data);
      }
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error(t('errorFetchingAnalytics'));
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
      green: 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400',
      purple: 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
      red: 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
    };

    const bgClass = colorClasses[color]?.split(' ').slice(0, 2).join(' ') || 'bg-blue-100 dark:bg-blue-900/20';
    const textClass = colorClasses[color]?.split(' ').slice(2).join(' ') || 'text-blue-600 dark:text-blue-400';

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 p-3 rounded-lg ${bgClass}`}>
            <Icon className={`h-6 w-6 ${textClass}`} />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{value}</h3>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-500">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            <BarChart3 className="inline w-5 h-5 mr-2" />
            {t('preferenceAnalytics')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('analyzePreferenceUsageAndTrends')}
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value={7}>{t('last7Days')}</option>
            <option value={30}>{t('last30Days')}</option>
            <option value={90}>{t('last90Days')}</option>
            <option value={365}>{t('lastYear')}</option>
          </select>
          
          <button
            onClick={fetchAnalytics}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('refresh')}
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('totalDefinitions')}
          value={usageStats.length}
          subtitle={t('preferenceDefinitions')}
          icon={Database}
          color="blue"
        />
        <StatCard
          title={t('customizedUsers')}
          value={analysisData.users_with_custom_preferences.length}
          subtitle={t('usersWithCustomPreferences')}
          icon={Users}
          color="green"
        />
        <StatCard
          title={t('customizedEntities')}
          value={analysisData.entities_with_custom_settings.length}
          subtitle={t('entitiesWithCustomSettings')}
          icon={Building}
          color="purple"
        />
        <StatCard
          title={t('unusedDefinitions')}
          value={analysisData.unused_preference_definitions.length}
          subtitle={t('definitionsNotInUse')}
          icon={AlertCircle}
          color="red"
        />
      </div>

      {/* Top Users with Custom Preferences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            <Users className="inline w-5 h-5 mr-2" />
            {t('topCustomizedUsers')}
          </h4>
          
          <div className="space-y-3">
            {analysisData.users_with_custom_preferences.slice(0, 10).map((user) => (
              <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    @{user.username}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {user.custom_preferences}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('customPreferences')}
                  </p>
                </div>
              </div>
            ))}
            
            {analysisData.users_with_custom_preferences.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                {t('noCustomizedUsersFound')}
              </p>
            )}
          </div>
        </div>

        {/* Top Entities with Custom Settings */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            <Building className="inline w-5 h-5 mr-2" />
            {t('topCustomizedEntities')}
          </h4>
          
          <div className="space-y-3">
            {analysisData.entities_with_custom_settings.slice(0, 10).map((entity) => (
              <div key={entity.id} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {entity.name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
                    {entity.custom_settings}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('customSettings')}
                  </p>
                </div>
              </div>
            ))}
            
            {analysisData.entities_with_custom_settings.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                {t('noCustomizedEntitiesFound')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Preference Adoption Rates */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          <Target className="inline w-5 h-5 mr-2" />
          {t('preferenceAdoptionRates')}
        </h4>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('preference')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('type')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('usage')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('adoptionRate')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {analysisData.popular_preferences.slice(0, 15).map((pref) => (
                <tr key={pref.preference_key} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                      {pref.preference_key}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      pref.preference_type === 'user_global' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' :
                      pref.preference_type === 'user_entity' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                      'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                    }`}>
                      {pref.preference_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {pref.usage_count} / {pref.total_possible}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(100, pref.adoption_rate)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-900 dark:text-white">
                        {pref.adoption_rate}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Active Users */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            <TrendingUp className="inline w-5 h-5 mr-2" />
            {t('mostActiveUsers')} ({selectedPeriod} {t('days')})
          </h4>
          
          <div className="space-y-3">
            {auditStats.top_users.map((user) => (
              <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    @{user.username}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">
                    {user.change_count}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('changes')}
                  </p>
                </div>
              </div>
            ))}
            
            {auditStats.top_users.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                {t('noActivityInPeriod')}
              </p>
            )}
          </div>
        </div>

        {/* Most Changed Preferences */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            <Calendar className="inline w-5 h-5 mr-2" />
            {t('mostChangedPreferences')} ({selectedPeriod} {t('days')})
          </h4>
          
          <div className="space-y-3">
            {auditStats.top_preferences.map((pref) => (
              <div key={`${pref.preference_key}-${pref.table_name}`} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                    {pref.preference_key}
                  </code>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {pref.table_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                    {pref.change_count}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('changes')}
                  </p>
                </div>
              </div>
            ))}
            
            {auditStats.top_preferences.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                {t('noActivityInPeriod')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Unused Preferences Warning */}
      {analysisData.unused_preference_definitions.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {t('unusedPreferenceDefinitionsWarning')}
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                {t('unusedPreferenceDefinitionsDescription', { 
                  count: analysisData.unused_preference_definitions.length 
                })}
              </p>
              <div className="mt-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {analysisData.unused_preference_definitions.slice(0, 8).map((pref) => (
                    <code key={pref.preference_key} className="bg-yellow-100 dark:bg-yellow-900/40 px-2 py-1 rounded text-xs">
                      {pref.preference_key}
                    </code>
                  ))}
                </div>
                {analysisData.unused_preference_definitions.length > 8 && (
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                    {t('andMoreUnused', { 
                      count: analysisData.unused_preference_definitions.length - 8 
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreferenceAnalytics;